/**
 * FilePreviewModal — universeller Datei-Viewer fuer PDF + Image + Fallback.
 *
 * Der Rahmen folgt A0: Kopf steht, nur `DialogBody` scrollt. Vorher war die Vorschau ein freies
 * `div` im Dialog — bei einer hohen Seite wanderte die Kopfleiste mit Seitenzaehler und
 * Download nach oben aus dem Fenster. Den Betrachter selbst liefert `FilePreviewBody`.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { I } from '@/shared/icons';
import type { PropertyFileResponse } from '@/shared/api';
import {
  detectPreviewKind,
  FilePreviewBody,
  PreviewMessage,
  PreviewPageNav,
} from './FilePreviewBody';
import { useFileBlob } from './useFileBlob';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  file: PropertyFileResponse | null;
  onDownload: (file: PropertyFileResponse) => void;
}

export function FilePreviewModal({ open, onOpenChange, propertyId, file, onDownload }: Props) {
  const fileId = file?.id ?? null;
  const { url, loading, error } = useFileBlob(propertyId, open ? fileId : null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  const kind = detectPreviewKind(file?.contentType ?? '');

  function handleClose(v: boolean) {
    onOpenChange(v);
    if (!v) {
      setPage(1);
      setPageCount(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl" showClose={false}>
        <DialogHeader className="relative flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <DialogTitle className="min-w-0 break-words pr-10 text-lg sm:truncate sm:pr-0">
            {file?.fileName ?? 'Vorschau'}
          </DialogTitle>
          <div className="flex shrink-0 items-center gap-1">
            {kind === 'pdf' && pageCount && pageCount > 1 && (
              <PreviewPageNav
                page={page}
                pageCount={pageCount}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
              />
            )}
            <Button
              variant="ghost"
              leftIcon={<I name="Download" size={14} />}
              onClick={() => file && onDownload(file)}
              disabled={!file}
            >
              Herunterladen
            </Button>
          </div>
          {/* Absolut positioniert, damit der Schliessen-Weg auf 360 px nicht in die zweite
              Zeile rutscht und dort mit „Herunterladen" um den Platz konkurriert. */}
          <button
            type="button"
            onClick={() => handleClose(false)}
            aria-label="Schließen"
            className="text-ink-soft hover:text-ink absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded"
          >
            <I name="Close" size={18} />
          </button>
        </DialogHeader>
        <DialogBody className="bg-card-alt/40 min-h-[40dvh] p-4">
          {loading && <PreviewMessage label="Lade Datei…" />}
          {error && <PreviewMessage label={error.message} tone="error" />}
          {!loading && !error && url && file && (
            <FilePreviewBody
              kind={kind}
              url={url}
              fileName={file.fileName}
              page={page}
              onPageCount={setPageCount}
              onDownload={() => onDownload(file)}
            />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
